import { ApexOptions } from 'apexcharts';
import { FC, useMemo } from 'react';
import Chart from 'react-apexcharts';

export interface TrendSeries {
  name: string;
  type: 'column' | 'line';
  data: number[];
  color: string;
}

export interface TrendChartYAxis {
  /** Which series names this y-axis applies to */
  seriesNames: string[];
  /** Format y-axis tick labels */
  formatter?: (val: number) => string;
  /** Maximum value for the axis */
  max?: number;
  /** Minimum value for the axis (default: 0) */
  min?: number;
  /** Number of tick marks */
  tickAmount?: number;
  /** Show on the right side */
  opposite?: boolean;
}

export interface TrendChartProps {
  /** Chart series data */
  series: TrendSeries[];
  /** Chart height in pixels */
  height?: number;
  /** X-axis category labels */
  xAxisLabels?: string[];
  /** X-axis title */
  xAxisTitle?: string;
  /** Y-axis configurations (supports dual axes) */
  yAxes?: TrendChartYAxis[];
  /** Custom tooltip value formatter */
  tooltipFormatter?: (
    val: number,
    opts: { seriesIndex: number; dataPointIndex: number }
  ) => string;
}

const STYLE = {
  AXIS_FONT_SIZE: '14px',
  AXIS_COLOR: '#4b5563', // gray-600
  GRID_COLOR: '#f3f4f6', // gray-100
  STROKE_DASH_ARRAY: 4,
  COLUMN_WIDTH: '60%',

  LINE_STROKE_WIDTH: 3,
} as const;

const TrendChart: FC<TrendChartProps> = ({
  series,
  height = 280,
  xAxisLabels,
  xAxisTitle,
  yAxes,
  tooltipFormatter,
}) => {
  const apexSeries: ApexAxisChartSeries = useMemo(
    () =>
      series.map((s) => ({
        name: s.name,
        type: s.type,
        data: s.data,
      })),
    [series]
  );

  const colors = useMemo(() => series.map((s) => s.color), [series]);

  const strokeWidths = useMemo(
    () => series.map((s) => (s.type === 'line' ? STYLE.LINE_STROKE_WIDTH : 0)),
    [series]
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'line',
        fontFamily: 'Inter, "Noto Color Emoji", sans-serif',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors,
      stroke: {
        width: strokeWidths,
        curve: 'straight',
      },
      plotOptions: {
        bar: {
          columnWidth: STYLE.COLUMN_WIDTH,
          borderRadius: 3,
          borderRadiusApplication: 'end',
        },
      },
      xaxis: {
        categories: xAxisLabels,
        axisBorder: { show: false },
        axisTicks: { show: false },
        title: xAxisTitle
          ? {
              text: xAxisTitle,
              style: {
                color: STYLE.AXIS_COLOR,
                fontSize: STYLE.AXIS_FONT_SIZE,
                fontWeight: 400,
              },
            }
          : undefined,
      },
      yaxis: yAxes?.map((axis) => ({
        seriesName: axis.seriesNames,
        opposite: axis.opposite,
        labels: {
          show: true,
          style: {
            colors: STYLE.AXIS_COLOR,
            fontSize: STYLE.AXIS_FONT_SIZE,
          },
          ...(axis.formatter ? { formatter: axis.formatter } : {}),
        },
        tickAmount: axis.tickAmount ?? 4,
        min: axis.min ?? 0,
        max: axis.max,
      })),
      legend: {
        position: 'top' as const,
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        shared: true,
        intersect: false,
        followCursor: true,
        ...(tooltipFormatter
          ? {
              y: {
                formatter: tooltipFormatter as (
                  val: number,
                  opts?: unknown
                ) => string,
              },
            }
          : {}),
      },
      grid: {
        show: true,
        borderColor: STYLE.GRID_COLOR,
        strokeDashArray: STYLE.STROKE_DASH_ARRAY,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
    }),
    [colors, strokeWidths, xAxisLabels, xAxisTitle, yAxes, tooltipFormatter]
  );

  return (
    <Chart type="line" series={apexSeries} options={options} height={height} />
  );
};

export default TrendChart;
